import React, { useEffect, useState } from 'react';

import { useDrawers } from 'hooks/useDrawers';
import { usePagination } from 'hooks/usePagination';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { GroupsService } from 'services/GroupsService';

import { message } from 'antd';
import {
  DELETE_CONFIRM,
  DELETE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';

import { UserService } from 'services/UserService';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { TableHeader } from 'components/TableHeader';
import { GroupsForm } from './GroupsForm';
import { GroupsTable } from './GroupsTable';

function Groups() {
  useDocumentTitle('Группы');
  const { setCount } = useHeaderChanger('Группы');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const { page, totalRows, perPage, setTotalRows, setPagination } =
    usePagination();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [page, perPage]);

  const fetchGroups = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await GroupsService.getGroups(page, perPage);

      setCount(total);
      setTotalRows(total);
      setGroups(items);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await UserService.getAllUsers();
      setUsers(data);
    } catch (err) {
      message.error(FETCH_ERROR);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await GroupsService.deleteGroups(activeRecord?.id);

      if (isDeleted) {
        closeDrawer();
        fetchGroups();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="groups" />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.title}
        pageId="groups"
        recordFields={[
          { label: 'Группа', key: 'title' },
          {
            label: 'Количество пользователей',
            key: 'users',
            computedValue: activeRecord?.users?.length,
          },
          {
            label: 'Ответственный группы',
            key: 'responsible_users',
            computedValue: activeRecord?.users
              ?.filter((user) => user?.isResponsible)
              .map((user) => user.full_name)
              .join(', '),
          },
          { label: 'Последние изменения', key: 'created' },
        ]}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText={DELETE_CONFIRM}
      />

      <GroupsTable
        items={groups}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
      />

      <GroupsForm
        isActive={formIsActive}
        initialValues={activeRecord}
        loading={loading}
        users={users}
        onSuccess={fetchGroups}
        onClose={closeForm}
      />
    </>
  );
}

export { Groups };
